import { useState, useEffect } from 'react';

/**
 * Module-level caches for two independent requests:
 *
 *   NAMES CACHE  — backed by the new lightweight GET /api/course/names/{userId}
 *   (2 columns: courseId + courseName, sorted by name, very fast)
 *   → populates the `courses` return value used by every dropdown.
 *
 *   RAW CACHE    — backed by the original GET /api/course/coursedomain/{userId}
 *   (full course objects including vendor + domains)
 *   → populates `rawCourses`, still needed by training/dialog.js to populate
 *     the domain/subdomain selects when a course is chosen in the training form.
 *
 * Both caches survive across mounts/unmounts (module scope) and deduplicate
 * in-flight requests so simultaneous callers share one fetch.
 */
let _cachedCourses = null;
let _inflightNamesPromise = null;

let _rawCache = null;
let _inflightRawPromise = null;

/** Clears module caches — test-only; not used in production. */
export function __resetCourseListCacheForTests() {
  _cachedCourses = null;
  _inflightNamesPromise = null;
  _rawCache = null;
  _inflightRawPromise = null;
}

function buildBasicAuthHeader() {
  const username = process.env.REACT_APP_API_USERNAME;
  const password = process.env.REACT_APP_API_PASSWORD;
  return `Basic ${btoa(`${username}:${password}`)}`;
}

/**
 * Fetches and caches course names (lightweight) + full raw objects separately.
 * Each cache is populated exactly once per browser session.
 *
 * Returns:
 *   courses        – [{id, name}]  from the new /api/course/names endpoint
 *   rawCourses     – full API objects (courseId, courseName, vendor, domains, …)
 *                    from the original /api/course/coursedomain endpoint
 *   coursesLoading – true while the names fetch is in-flight
 *
 * Usage:
 *   const { courses, coursesLoading } = useCourseList();  // dropdown
 *   const { rawCourses }             = useCourseList();  // training form (needs domains)
 */
export function useCourseList() {
  const [courses, setCourses] = useState(_cachedCourses ?? []);
  const [rawCourses, setRawCourses] = useState(_rawCache ?? []);
  const [coursesLoading, setCoursesLoading] = useState(_cachedCourses === null);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const apiUrl = process.env.REACT_APP_API_URL;
    const authHeader = buildBasicAuthHeader();

    // ── 1. Lightweight names fetch (drives the `courses` dropdown list) ──────
    if (_cachedCourses !== null) {
      setCourses(_cachedCourses);
      setCoursesLoading(false);
    } else {
      if (!_inflightNamesPromise) {
        _inflightNamesPromise = fetch(
          `${apiUrl}/api/course/names/${userId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: authHeader,
            },
          }
        )
          .then(r => r.json())
          .then(data => {
            const raw = Array.isArray(data) ? data : (data?.data ?? []);
            _cachedCourses = raw.map(c => ({ id: c.courseId, name: c.courseName }));
            return _cachedCourses;
          })
          .catch(() => {
            // Allow a retry on next mount after a transient failure.
            _inflightNamesPromise = null;
            return [];
          });
      }

      _inflightNamesPromise.then(c => {
        setCourses(c);
        setCoursesLoading(false);
      });
    }

    // ── 2. Full-object fetch (drives `rawCourses` used by training/dialog.js) ─
    // Fetched lazily in the background — does not affect coursesLoading.
    if (_rawCache !== null) {
      setRawCourses(_rawCache);
    } else {
      if (!_inflightRawPromise) {
        _inflightRawPromise = fetch(
          `${apiUrl}/api/course/coursedomain/${userId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: authHeader,
            },
          }
        )
          .then(r => r.json())
          .then(data => {
            _rawCache = Array.isArray(data) ? data : (data?.data ?? []);
            return _rawCache;
          })
          .catch(() => {
            _inflightRawPromise = null;
            return [];
          });
      }

      _inflightRawPromise.then(r => {
        setRawCourses(r);
      });
    }
  }, []);

  return { courses, rawCourses, coursesLoading };
}
